import { sansRenvoiErrone } from '../utils/renvois.js';
import { memo, useEffect, useId, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { MarkdownRenderer } from './MarkdownRenderer';
import { ReasoningPanel } from './ReasoningPanel';
import { MessageActions } from './MessageActions';
import { FileAttachments } from './FileAttachments';
import { CopyButton } from './CopyButton';
import { Badge } from './Badge';
import { Button } from './Button';
import { BrandMark } from './BrandMark';
import { MessageVisuals } from './visuals/MessageVisuals';
import { ReportMessage } from './report/ReportMessage';
import { CompactionMarker } from './CompactionMarker';
import { effortSummary, effortTooltip } from '../utils/effortPreference.js';
import { hasVisibleReasoning } from '../hooks/messageStream';
import { AlertIcon, PencilIcon, RefreshIcon, StopIcon } from './icons';
import './ChatMessage.css';
import './visuals/map/LiveMapCard.css';

/**
 * Niveau d'effort du pied de réponse (sans panneau de raisonnement). La raison du choix
 * (info-bulle à la souris) est aussi lue par les lecteurs d'écran (texte masqué) et
 * s'affiche au toucher ou au clavier : le libellé est un petit bouton qui la déplie.
 */
function EffortNote({ effort }) {
  const [open, setOpen] = useState(false);
  const reasonId = useId();
  const text = effortSummary(effort);
  if (!text) return null;
  const reason = effortTooltip(effort);
  if (!reason || reason === text) return <span className="message-effort">{text}</span>;
  return (
    <span className="message-effort-wrap">
      <button
        type="button"
        className="message-effort message-effort--button"
        title={reason}
        aria-expanded={open}
        aria-controls={open ? reasonId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {text}
        <span className="sr-only">{` (${reason})`}</span>
      </button>
      {open && (
        <span id={reasonId} className="message-effort__reason">
          {reason}
        </span>
      )}
    </span>
  );
}

const nf = new Intl.NumberFormat('fr-FR');

// Messages d'erreur selon le code renvoyé par le serveur (contrat C0, §1.2)
const ERROR_TITLES = {
  TRUNCATED: 'Réponse tronquée : la limite de longueur a été atteinte.',
  TOO_MANY_STEPS: "Trop d'étapes : la question a été interrompue.",
};

/**
 * Modification d'une question (UI-14) : le texte remplace la bulle ; l'envoi relance
 * la conversation à partir de ce point (les échanges suivants sont remplacés).
 */
function UserMessageEditor({ initial, hasFollowing, onCancel, onSubmit }) {
  const [text, setText] = useState(initial);
  const ref = useRef(null);
  const hintId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  // Hauteur ajustée au texte
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 280)}px`;
  }, [text]);

  const submit = () => {
    if (text.trim()) onSubmit(text);
  };

  return (
    <form
      className="message-edit"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <label className="sr-only" htmlFor={`${hintId}-input`}>Modifier la question</label>
      <textarea
        id={`${hintId}-input`}
        ref={ref}
        value={text}
        rows={2}
        aria-describedby={hintId}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            onCancel();
          } else if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            submit();
          }
        }}
      />
      <p id={hintId} className="message-edit__hint">
        {hasFollowing
          ? 'La réponse et les échanges qui suivent seront remplacés.'
          : 'Une nouvelle réponse sera générée.'}
        {' '}<kbd>Entrée</kbd> pour envoyer, <kbd>Échap</kbd> pour annuler.
      </p>
      <div className="message-edit__actions">
        <Button size="sm" className="message-edit__button" onClick={onCancel}>Annuler</Button>
        <Button type="submit" variant="primary" size="sm" className="message-edit__button" disabled={!text.trim()}>
          Envoyer
        </Button>
      </div>
    </form>
  );
}

/**
 * Un message du fil. Props d'action (toutes stables) :
 * - onRate(id, rating, details) : avis 👍 / 👎
 * - onRegenerate(id) : relance la question qui précède cette réponse (dernière réponse)
 * - onEdit(id, texte) : modifie une question et relance à partir de ce point ; renvoie
 *   false si l'envoi est refusé (hors ligne) : l'éditeur reste ouvert
 * `isLast` : dernier message du fil ; `busy` : une réponse est en cours.
 * `showCompaction` : faux quand le marqueur répète celui d'une réponse précédente (résumé réutilisé).
 */
export const ChatMessage = memo(function ChatMessage({
  message, index, onRate, onRegenerate, onEdit, isLast = false, hasFollowing = false, busy = false, showCompaction = true,
}) {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const [editing, setEditing] = useState(false);
  const editButtonRef = useRef(null);

  const showReasoning = isAssistant && hasVisibleReasoning(message);

  // Question envoyée, aucun événement visible encore : l'en-tête du raisonnement patiente seul
  // (logo déjà tracé, phase, chronomètre depuis le clic), dès la première image (lot 1)
  const isThinking = isAssistant && message.isStreaming && !message.content && !showReasoning && !message.artifacts?.length;

  const isFinished = isAssistant && !message.isStreaming;

  // Modèle ayant produit la réponse (libellé du registre, sinon identifiant)
  const modelName = isAssistant
    ? message.modelLabel || message.modelId || message.model
    : null;

  const usage = message.usage;

  // Réessayer / relancer : dernière réponse seulement, aucune autre réponse en cours
  const canRerun = isAssistant && isLast && !busy && !!onRegenerate;
  const rerun = () => onRegenerate?.(message.id);

  const cancelEdit = () => {
    setEditing(false);
    requestAnimationFrame(() => editButtonRef.current?.focus());
  };

  // Événement de la carte live (entrée ou sortie d'une zone de surveillance) : note « système »
  if (message.origin === 'carte') {
    const genre = message.evenement?.genre === 'sortie' ? 'sortie' : 'entree';
    return (
      <motion.div
        className="message assistant message--report"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.24, ease: [0, 0, 0, 1] }}
      >
        <div className="message-container">
          <p className={`lm-event is-${genre}`} role="status">
            <span className="lm-event__kicker">{genre === 'entree' ? 'Entrée de zone' : 'Sortie de zone'}</span>
            <span>{message.content}</span>
          </p>
        </div>
      </motion.div>
    );
  }

  // Rapport composé (DOC-17) : message « système », sans avatar ni actions de réponse
  if (message.origin === 'report') {
    return (
      <motion.div
        className="message assistant message--report"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.24, ease: [0, 0, 0, 1] }}
      >
        <div className="message-container">
          <ReportMessage message={message} />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className={`message ${message.role}`}
      // Réponse qui commence : visible tout de suite, sans fondu ni retard (lot 1)
      initial={message.isStreaming ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.24,
        delay: Math.min(index, 4) * 0.04,
        ease: [0, 0, 0, 1]
      }}
    >
      {/* Contexte condensé avant cette réponse (sprint 6) : séparateur au-dessus */}
      {isAssistant && showCompaction && message.compaction && <CompactionMarker compaction={message.compaction} />}

      <div className="message-container">
        {isAssistant && (
          <div className="message-avatar" aria-hidden="true">
            <BrandMark size={14} />
          </div>
        )}

        <div className={`message-content-wrapper ${editing ? 'is-editing' : ''}`}>

          {/* ── En-tête : nom + modèle (la bulle utilisateur n'en affiche pas) ── */}
          {isUser ? (
            <span className="sr-only">Vous :</span>
          ) : (
            <div className="message-header">
              <span className="message-role">Assistant</span>
              {isAssistant && message.model && (
                <Badge tone="neutral" className="message-model">digiTrans LLM</Badge>
              )}
            </div>
          )}

          {/* ── Raisonnement : étapes, phrases déplacées, réflexion résumée ; avant le premier
               événement visible, son en-tête seul (même composant, même loader ensuite) ── */}
          {(showReasoning || isThinking) && (
            <ReasoningPanel
              reasoning={message.reasoning}
              messageId={message.id}
              effort={message.effort}
              phase={message.isStreaming ? message.phase : null}
              pending={isThinking}
            />
          )}

          {/* ── Contenu du message ──────────────────────────────── */}
          {isUser && editing ? (
            <UserMessageEditor
              initial={message.content}
              hasFollowing={hasFollowing}
              onCancel={cancelEdit}
              onSubmit={(text) => {
                // Envoi refusé (hors ligne : onEdit renvoie false) : le texte modifié reste à l'écran
                if (onEdit?.(message.id, text) === false) return;
                setEditing(false);
              }}
            />
          ) : typeof message.content === 'string' ? (
            (message.content || isUser) && (
              <div className="message-content">
                {isUser ? (
                  <>
                    {message.selection?.nom && (
                      <span className="lm-chip lm-chip--sent" title={message.selection.document ? 'Section sélectionnée dans le document' : 'Élément sélectionné sur la carte live'}>
                        {message.selection.document ? `Section « ${message.selection.nom} »` : `${message.selection.nom} · sélectionné sur la carte`}
                      </span>
                    )}
                    <p className="message-user-text">{message.content}</p>
                  </>
                ) : (
                  <MarkdownRenderer
                    content={sansRenvoiErrone(message.content)}
                    isStreaming={message.isStreaming}
                    idPrefix={message.id}
                  />
                )}

                {/* Curseur clignotant pendant l'arrivée du texte */}
                {message.isStreaming && (
                  <span className="message-cursor" aria-hidden="true" />
                )}
              </div>
            )
          ) : (
            <pre className="message-raw">{JSON.stringify(message.content, null, 2)}</pre>
          )}

          {/* ── Visuels : sous la réponse, dans l'ordre de production (skeleton → carte) ── */}
          {isAssistant && message.artifacts?.length > 0 && (
            <MessageVisuals artifacts={message.artifacts} messageId={message.id} />
          )}

          {/* ── Actions d'une question : copier, modifier ────────── */}
          {isUser && !editing && (
            <div className="message-user-actions">
              <CopyButton getText={() => message.content} label="Copier la question" className="message-action" />
              {onEdit && (
                <Button
                  ref={editButtonRef}
                  variant="ghost"
                  size="sm"
                  icon={PencilIcon}
                  iconOnly
                  className="message-action"
                  aria-label="Modifier la question"
                  title={busy ? 'Disponible à la fin de la réponse' : 'Modifier la question'}
                  disabled={busy}
                  onClick={() => setEditing(true)}
                />
              )}
            </div>
          )}

          {/* ── Erreur (troncature, plafond d'étapes, réseau…) ──── */}
          {isAssistant && message.error && (
            <div className="message-error" role="alert">
              <AlertIcon size={16} />
              <span className="message-error__text">
                {ERROR_TITLES[message.error.code] ?? message.error.message}
              </span>
              {canRerun && (
                <Button size="sm" tone="danger" icon={RefreshIcon} className="message-retry" onClick={rerun}>
                  Réessayer
                </Button>
              )}
            </div>
          )}

          {/* Stop : issue portée par le message, avec ou sans raisonnement (Qwen) */}
          {isAssistant && message.outcome === 'stopped' && !message.error && (
            <div className="message-stopped">
              <StopIcon size={14} />
              <span>
                {message.content || showReasoning
                  ? 'Réponse interrompue.'
                  : 'Réponse interrompue avant le premier retour.'}
              </span>
              {canRerun && (
                <Button size="sm" icon={RefreshIcon} className="message-retry" onClick={rerun}>
                  Relancer
                </Button>
              )}
            </div>
          )}

          {/* ── Fichiers générés : aperçu dans le panneau + téléchargement ── */}
          {isFinished && <FileAttachments content={message.content} messageId={message.id} />}

          {/* ── Barre d'actions + modèle + stats tokens ─────────── */}
          {isFinished && (message.content || message.artifacts?.length || usage || modelName) && (
            <div className="message-footer">
              {message.content ? (
                <MessageActions
                  message={message}
                  onRate={onRate}
                  onRegenerate={isLast && onRegenerate ? rerun : null}
                  busy={busy}
                />
              ) : <span />}
              <div className="message-meta">
                {/* Niveau d'effort sans panneau de raisonnement (modèle sans réflexion…) */}
                {!showReasoning && <EffortNote effort={message.effort} />}
                {modelName && (
                  <span className="message-model-used" title="Modèle ayant produit cette réponse">
                    {modelName}
                  </span>
                )}
                {usage && (
                  <div className="usage-stats ds-num" title="Tokens cumulés sur toutes les étapes">
                    <span className="usage-stat">
                      <span className="usage-label">In:</span>
                      <span className="usage-value">{nf.format(usage.input_tokens ?? 0)}</span>
                    </span>
                    <span className="usage-divider">•</span>
                    <span className="usage-stat">
                      <span className="usage-label">Out:</span>
                      <span className="usage-value">{nf.format(usage.output_tokens ?? 0)}</span>
                    </span>
                    {usage.cache_read_input_tokens > 0 && (
                      <>
                        <span className="usage-divider">•</span>
                        <span className="usage-stat">
                          <span className="usage-label">Cache:</span>
                          <span className="usage-value">{nf.format(usage.cache_read_input_tokens)}</span>
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </motion.div>
  );
});
